(() => {
  function mount(root) {
    const galleries = root.matches?.('#MainContent .ar-product-scope media-gallery')
      ? [root] : root.querySelectorAll?.('#MainContent .ar-product-scope media-gallery') || [];
    galleries.forEach((gallery) => {
      const track = gallery.querySelector('.ar-media-track');
      if (!track || track.dataset.arDragBound) return;
      track.dataset.arDragBound = '1';
      const previous = gallery.querySelector('.ar-media-viewer .slider-button--prev');
      const next = gallery.querySelector('.ar-media-viewer .slider-button--next');
      const segments = [...gallery.querySelectorAll('.ar-gallery-progress__segment')];
      const slides = () => [...track.querySelectorAll('.product__media-item')].filter((slide) => slide.offsetWidth);
      const nearest = (items) => items.reduce((best, slide, index) =>
        Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(items[best].offsetLeft - track.scrollLeft) ? index : best, 0);
      let destination = 0;
      let animation = 0; // non-zero while a programmatic slide is under way
      let frame = 0;
      let settleTimer = 0;

      function sync() {
        const items = slides();
        if (!items.length) return;
        const visible = nearest(items);
        if (previous) previous.disabled = (animation ? destination : visible) === 0;
        if (next) next.disabled = (animation ? destination : visible) === items.length - 1;
        segments.forEach((segment, index) => segment.classList.toggle('is-active', index === visible));
        // the thumbnail outline follows the photo right away (the theme's own update waits 0.5s after the slide stops)
        const id = items[animation ? destination : visible]?.dataset.mediaId;
        const thumb = id && gallery.querySelector(`[id^="GalleryThumbnails"] [data-target="${id}"] button`);
        if (thumb && !thumb.hasAttribute('aria-current')) {
          gallery.querySelectorAll('[id^="GalleryThumbnails"] button[aria-current]').forEach((b) => b.removeAttribute('aria-current'));
          thumb.setAttribute('aria-current', 'true');
        }
      }

      function settle() {
        clearTimeout(settleTimer);
        if (!animation) return;
        animation = 0;
        track.classList.remove('is-animating');
        sync();
      }

      // jumping several photos (thumbnail click): the old photo fades out over the new one - a true cross-fade, never a blank frame
      function crossFade(slide) {
        const cover = document.createElement('div');
        cover.setAttribute('aria-hidden', 'true');
        cover.style.cssText = `position:absolute;z-index:1;pointer-events:none;overflow:hidden;top:${track.offsetTop}px;left:${track.offsetLeft}px;width:${track.offsetWidth}px;height:${track.offsetHeight}px;transition:opacity .38s ease`;
        const copy = slide.cloneNode(true);
        copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        copy.removeAttribute('id');
        copy.style.cssText = 'width:100%;height:100%;margin:0';
        cover.appendChild(copy);
        track.after(cover);
        requestAnimationFrame(() => requestAnimationFrame(() => { cover.style.opacity = '0'; }));
        setTimeout(() => cover.remove(), 450);
      }

      function navigate(requested) {
        const items = slides();
        const index = typeof requested === 'number' ? requested : items.indexOf(requested);
        if (index < 0 || !items.length) return;
        const current = animation ? destination : nearest(items);
        destination = Math.min(items.length - 1, Math.max(0, index));
        const to = items[destination].offsetLeft;
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reduce && Math.abs(destination - current) > 1) {
          crossFade(items[current]);
          animation = 0;
          track.classList.add('is-animating');
          track.scrollLeft = to;
          requestAnimationFrame(() => { track.classList.remove('is-animating'); sync(); });
          return;
        }
        if (Math.abs(track.scrollLeft - to) < 1) { animation = 1; settle(); return; }
        // one photo over: the browser's own smooth scroll runs on the compositor, so it stays fluid while big photos decode
        animation = 1;
        track.classList.add('is-animating');
        track.scrollTo({ left: to, behavior: reduce ? 'auto' : 'smooth' });
        clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, 900);
        sync();
      }

      track.addEventListener('scrollend', () => {
        if (animation && Math.abs(track.scrollLeft - (slides()[destination]?.offsetLeft ?? track.scrollLeft)) < 2) settle();
      });
      track.arNavigateTo = navigate;
      track.addEventListener('scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => { frame = 0; sync(); });
      }, { passive: true });
      gallery.querySelector('.ar-media-viewer .slider-buttons')?.addEventListener('click', (event) => {
        const button = event.target.closest('.slider-button');
        if (button !== previous && button !== next) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const items = slides();
        navigate((animation ? destination : nearest(items)) + (button === next ? 1 : -1));
      }, true);

      let pointer = null;
      let startX = 0;
      let startScroll = 0;
      let startIndex = 0;
      let dragged = false;
      let suppressClick = false;
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || track.scrollWidth <= track.clientWidth) return;
        if (event.target.closest('video, model-viewer, deferred-media, iframe')) return;
        pointer = event.pointerId;
        startX = event.clientX;
        startIndex = animation ? destination : nearest(slides());
        if (animation) { track.scrollTo({ left: track.scrollLeft }); settle(); }
        startScroll = track.scrollLeft;
        dragged = false;
      });
      track.addEventListener('pointermove', (event) => {
        if (pointer !== event.pointerId) return;
        if (!dragged && Math.abs(event.clientX - startX) >= 6) {
          dragged = true;
          track.setPointerCapture(pointer);
          track.classList.add('is-dragging', 'is-animating');
        }
        if (!dragged) return;
        event.preventDefault();
        // the photo follows the mouse; snapping waits until release
        track.scrollLeft = startScroll - (event.clientX - startX);
      });
      function release(event) {
        if (pointer !== event.pointerId) return;
        pointer = null;
        if (!dragged) return;
        dragged = false;
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        track.classList.remove('is-dragging');
        const distance = event.clientX - startX;
        navigate(event.type !== 'pointercancel' && Math.abs(distance) >= 40 ? startIndex + (distance < 0 ? 1 : -1) : startIndex);
        suppressClick = true;
        setTimeout(() => { suppressClick = false; }, 0);
      }
      track.addEventListener('pointerup', release);
      track.addEventListener('pointercancel', release);
      track.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClick = false;
      }, true);
      track.addEventListener('dragstart', (event) => {
        if (event.target.closest('img')) event.preventDefault();
      });
      sync();
      // load the other photos once the page is idle, so sliding never shows an empty frame
      // ...and decode them ahead of time, so a photo sliding in for the first time doesn't stall the slide
      // only the next two after load (mobile data); all of them once the shopper starts swiping
      const warm = (n) => [...track.querySelectorAll('img')].slice(0, n).forEach((img) => {
        if (img.loading === 'lazy') img.loading = 'eager';
        if (img.decode) img.decode().catch(() => {});
      });
      const early = () => (window.requestIdleCallback || setTimeout)(() => warm(3));
      if (document.readyState === 'complete') early();
      else window.addEventListener('load', early, { once: true });
      ['pointerdown', 'scroll', 'keydown'].forEach((t) => track.addEventListener(t, () => warm(Infinity), { once: true, passive: true }));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
